import {
  Badge,
  Button,
  EmptyState,
  EnvBadge,
  Icon,
  Input,
  PageHeader,
  Skeleton,
  Textarea,
  When,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { FadGroupPicker } from '@/features/release-config/FadGroupPicker';
import { RequiredArtifactsEditor } from '@/features/release-config/RequiredArtifactsEditor';
import type {
  DeployConfig,
  ReleaseChannelInfo,
  ReleaseEnvironment,
  ReleaseProductInfo,
} from '@/features/release-config/api';
import {
  useFadTesterGroups,
  useReleaseIntegrations,
  useReleaseProducts,
  useSendSlackTest,
  useSyncFadGroups,
  useUpdateReleaseChannel,
  useUpdateReleaseProduct,
} from '@/features/release-config/hooks';
import { ReleaseStatusBadge } from '@/features/releases/ReleasesTable';
import { relativeTime } from '@/lib/relative-time';
import { A, useParams } from '@solidjs/router';
import {
  ApiClientError,
  RELEASE_ADAPTER_LABELS,
  type UpdateReleaseChannelBody,
} from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

const errorMessage = (err: unknown): string =>
  err instanceof ApiClientError ? err.error.message : 'Request failed';

/** Read-only per-environment summary: published version + most recent release, with a link to the filtered list. */
const EnvironmentSummary = (props: {
  product: ReleaseProductInfo;
  channel: ReleaseChannelInfo;
}) => {
  const latest = () => props.channel.latestRelease;
  return (
    <A
      href={`/release/versions?product=${props.product.id}&environment=${props.channel.environment}`}
      class="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-sm transition-colors duration-200 ease-out hover:bg-neutral-100 motion-reduce:transition-none dark:hover:bg-neutral-800"
    >
      <span class="flex items-center gap-3">
        <EnvBadge env={props.channel.environment} />
        <span class="text-xs text-neutral-500">published {props.channel.versionFloor ?? '—'}</span>
      </span>
      <Show
        when={latest()}
        fallback={<span class="text-xs text-neutral-400">no releases yet</span>}
      >
        {(r) => (
          <span class="flex items-center gap-2">
            <span>
              {r().version} <span class="text-neutral-400">#{r().buildNumber}</span>
            </span>
            <ReleaseStatusBadge status={r().status} />
            <When date={r().createdAt} />
          </span>
        )}
      </Show>
    </A>
  );
};

/** Per-product build counter — one monotonic sequence across all environments (UCH-293). */
const BuildCounterCard = (props: { product: ReleaseProductInfo; canManage: boolean }) => {
  const toast = useToast();
  const update = useUpdateReleaseProduct();
  const [draft, setDraft] = createSignal<string | null>(null);
  const value = () => draft() ?? String(props.product.nextBuildNumber);
  const hint = () =>
    `Must be greater than the highest existing build. BUILD_START_FROM_${props.product.slug.toUpperCase().replace(/-/g, '_')} may raise it again at boot.`;

  const save = async () => {
    const next = Number.parseInt(value(), 10);
    if (!Number.isInteger(next) || next < 1) {
      toast.error('Next build number must be a positive integer');
      return;
    }
    try {
      await update.mutateAsync({ productId: props.product.id, body: { nextBuildNumber: next } });
      setDraft(null);
      toast.success('Build counter saved');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
      <h2 class="mb-2 font-medium">Build counter</h2>
      <Show
        when={props.canManage}
        fallback={
          <p class="text-sm">
            Next build number <span class="font-medium">#{props.product.nextBuildNumber}</span>
            <span class="ml-2 text-xs text-neutral-500">
              one sequence per product across all environments
            </span>
          </p>
        }
      >
        <div class="flex items-start gap-2">
          <div class="w-56">
            <Input
              label="Next build number"
              type="number"
              min="1"
              value={value()}
              onInput={(e) => setDraft(e.currentTarget.value)}
              hint={hint()}
            />
          </div>
          <Button variant="secondary" class="mt-6" onClick={save} loading={update.isPending}>
            Save
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** Branches the ref picker lists first (e.g. dev, sit, uat, prd), comma-separated. */
const PinnedBranchesCard = (props: { product: ReleaseProductInfo; canManage: boolean }) => {
  const toast = useToast();
  const update = useUpdateReleaseProduct();
  const [draft, setDraft] = createSignal<string | null>(null);
  const value = () => draft() ?? props.product.pinnedBranches.join(', ');

  const save = async () => {
    const pinnedBranches = [
      ...new Set(
        value()
          .split(',')
          .map((b) => b.trim())
          .filter(Boolean),
      ),
    ];
    try {
      await update.mutateAsync({ productId: props.product.id, body: { pinnedBranches } });
      setDraft(null);
      toast.success('Default branches saved');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
      <h2 class="mb-2 font-medium">Default branches</h2>
      <Show
        when={props.canManage}
        fallback={
          <p class="text-sm">
            {props.product.pinnedBranches.join(', ') || (
              <span class="text-neutral-500">None configured</span>
            )}
          </p>
        }
      >
        <div class="flex items-start gap-2">
          <div class="w-96">
            <Input
              label="Branches"
              value={value()}
              onInput={(e) => setDraft(e.currentTarget.value)}
              placeholder="dev, sit, uat, prd"
              hint="Comma-separated; listed first in the Branch tab when picking a ref."
            />
          </div>
          <Button variant="secondary" class="mt-6" onClick={save} loading={update.isPending}>
            Save
          </Button>
        </div>
      </Show>
    </div>
  );
};

/**
 * Per-environment Tauri package id + required-artifact matrix (desktop products only).
 * Keyed by the environment string, never the channel object: a products refetch must not
 * remount the section and wipe in-flight edits (UCH-295). Fields hold a dirty draft that
 * wins over server data until the field's own save succeeds.
 */
/** UCH-418: per-env Firebase App Distribution config (mobile products). */
const MobileDistributionSection = (props: {
  product: ReleaseProductInfo;
  environment: ReleaseEnvironment;
  canManage: boolean;
}) => {
  const toast = useToast();
  const update = useUpdateReleaseChannel();
  const sync = useSyncFadGroups();
  // Same query the picker uses (shared cache) — here only for syncedAt.
  const fadGroups = useFadTesterGroups(() => ({
    productId: props.product.id,
    environment: props.environment,
  }));
  const channel = () => props.product.channels.find((c) => c.environment === props.environment);
  const [androidDraft, setAndroidDraft] = createSignal<string | null>(null);
  const [iosDraft, setIosDraft] = createSignal<string | null>(null);
  const [groupsDraft, setGroupsDraft] = createSignal<string[] | null>(null);
  const androidId = () => androidDraft() ?? channel()?.firebaseAppId ?? '';
  const iosId = () => iosDraft() ?? channel()?.firebaseAppIdIos ?? '';
  const groups = () => groupsDraft() ?? channel()?.fadTesterGroups ?? [];

  // Only dirty drafts go in the PATCH body: sending display fallbacks wrote '' over
  // saved config whenever an input was blank or channel() hadn't loaded (UCH-433).
  const save = async () => {
    const body: UpdateReleaseChannelBody = {};
    const android = androidDraft();
    if (android !== null) body.firebaseAppId = android.trim();
    const ios = iosDraft();
    if (ios !== null) body.firebaseAppIdIos = ios.trim();
    const groupsPicked = groupsDraft();
    if (groupsPicked !== null) body.fadTesterGroups = groupsPicked;
    if (Object.keys(body).length === 0) return;
    try {
      await update.mutateAsync({
        productId: props.product.id,
        environment: props.environment,
        body,
      });
      setAndroidDraft(null);
      setIosDraft(null);
      setGroupsDraft(null);
      toast.success(`${props.environment.toUpperCase()} channel saved`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const onSync = async () => {
    try {
      await sync.mutateAsync({ productId: props.product.id, environment: props.environment });
      toast.success(`${props.environment.toUpperCase()} tester groups synced`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const syncedAt = () => fadGroups.data?.data.syncedAt ?? null;
  const hasAppIds = () => Boolean(channel()?.firebaseAppId || channel()?.firebaseAppIdIos);

  return (
    <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <div class="mb-2 flex items-center justify-between gap-2">
        <h3>
          <EnvBadge env={props.environment} />
        </h3>
        <Show when={props.canManage}>
          <div class="flex items-center gap-2">
            <Show when={syncedAt()}>
              {(at) => <span class="text-xs text-neutral-500">Synced {relativeTime(at())}</span>}
            </Show>
            <Button
              size="sm"
              variant="ghost"
              onClick={onSync}
              disabled={sync.isPending || !hasAppIds()}
              title={hasAppIds() ? undefined : 'Configure the Firebase app ids first'}
              aria-label={`${props.environment} sync tester groups`}
            >
              <Icon
                name="rotate"
                class={sync.isPending ? 'animate-spin motion-reduce:animate-none' : undefined}
              />
              Sync
            </Button>
          </div>
        </Show>
      </div>
      {/* Placeholders instead of hints so items-end lines the Save button up with the inputs. */}
      <div class="flex items-end gap-2">
        <div class="flex-1">
          <Input
            label="Firebase app id (Android)"
            placeholder="1:123456789:android:abc123"
            value={androidId()}
            onInput={(e) => setAndroidDraft(e.currentTarget.value)}
            disabled={!props.canManage}
            aria-label={`${props.environment} Firebase app id (Android)`}
          />
        </div>
        <div class="flex-1">
          <Input
            label="Firebase app id (iOS)"
            placeholder="1:123456789:ios:def456"
            value={iosId()}
            onInput={(e) => setIosDraft(e.currentTarget.value)}
            disabled={!props.canManage}
            aria-label={`${props.environment} Firebase app id (iOS)`}
          />
        </div>
        <Show when={props.canManage}>
          <Button variant="secondary" onClick={save} loading={update.isPending}>
            Save
          </Button>
        </Show>
      </div>
      {/* Groups get their own full-width row — the wrap list is too tall for the input row. */}
      <div class="mt-3">
        <FadGroupPicker
          productId={props.product.id}
          environment={props.environment}
          value={groups()}
          onChange={setGroupsDraft}
          disabled={!props.canManage}
          label="Default tester groups"
        />
      </div>
    </div>
  );
};

const DesktopUpdaterSection = (props: {
  product: ReleaseProductInfo;
  environment: ReleaseEnvironment;
  canManage: boolean;
}) => {
  const toast = useToast();
  const update = useUpdateReleaseChannel();
  const channel = () => props.product.channels.find((c) => c.environment === props.environment);
  const [draft, setDraft] = createSignal<string | null>(null);
  const value = () => draft() ?? channel()?.tauriPackageId ?? '';

  const save = async () => {
    // Same guard as the mobile card (UCH-433): an untouched Save must not write
    // the display fallback ('' when channel() hasn't loaded) back to the DB.
    const dirty = draft();
    if (dirty === null) return;
    try {
      await update.mutateAsync({
        productId: props.product.id,
        environment: props.environment,
        body: { tauriPackageId: dirty.trim() },
      });
      setDraft(null);
      toast.success(`${props.environment.toUpperCase()} channel saved`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <h3 class="mb-2">
        <EnvBadge env={props.environment} />
      </h3>
      <div class="flex items-end gap-2">
        <div class="flex-1">
          <Input
            label="Tauri package id"
            value={value()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            disabled={!props.canManage}
            aria-label={`${props.environment} Tauri package id`}
          />
        </div>
        <Show when={props.canManage}>
          <Button variant="secondary" onClick={save} loading={update.isPending}>
            Save
          </Button>
        </Show>
      </div>
      <RequiredArtifactsEditor
        productId={props.product.id}
        environment={props.environment}
        canManage={props.canManage}
      />
    </div>
  );
};

/** UCH-804: per-environment Slack channel for build notifications. */
const SlackChannelSection = (props: {
  product: ReleaseProductInfo;
  environment: ReleaseEnvironment;
  canManage: boolean;
  configured: boolean;
}) => {
  const toast = useToast();
  const update = useUpdateReleaseChannel();
  const sendTest = useSendSlackTest();
  const channel = () => props.product.channels.find((c) => c.environment === props.environment);
  const [draft, setDraft] = createSignal<string | null>(null);
  const value = () => draft() ?? channel()?.slackChannelId ?? '';
  // Tests the saved id, so it needs one and no unsaved edit.
  const canTest = () =>
    props.configured && (channel()?.slackChannelId ?? '') !== '' && draft() === null;

  const test = async () => {
    try {
      await sendTest.mutateAsync({ productId: props.product.id, environment: props.environment });
      toast.success(`Test message sent to ${props.environment.toUpperCase()} Slack channel`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const save = async () => {
    // Untouched Save must not write the display fallback back (UCH-433 guard).
    const dirty = draft();
    if (dirty === null) return;
    try {
      await update.mutateAsync({
        productId: props.product.id,
        environment: props.environment,
        body: { slackChannelId: dirty.trim() },
      });
      setDraft(null);
      toast.success(`${props.environment.toUpperCase()} Slack channel saved`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div class="flex items-end gap-2 rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <div class="w-16 pb-2">
        <EnvBadge env={props.environment} />
      </div>
      <div class="flex-1">
        <Input
          label="Channel id"
          placeholder="C0123456789"
          value={value()}
          onInput={(e) => setDraft(e.currentTarget.value)}
          disabled={!props.canManage || !props.configured}
          aria-label={`${props.environment} Slack channel id`}
        />
      </div>
      <Show when={props.canManage}>
        <Button
          variant="secondary"
          onClick={save}
          loading={update.isPending}
          disabled={!props.configured}
        >
          Save
        </Button>
        <Button
          variant="secondary"
          onClick={test}
          loading={sendTest.isPending}
          disabled={!canTest()}
        >
          Send test
        </Button>
      </Show>
    </div>
  );
};

// Publish = deploy: server products deploy through the GitOps set-image
// workflow (+ optional ArgoCD sync), web products through a publish workflow.
const DEPLOY_FIELDS: Record<
  DeployConfig['kind'],
  readonly { key: string; label: string; placeholder: string; required?: boolean }[]
> = {
  gitops: [
    {
      key: 'repo',
      label: 'GitOps repo',
      placeholder: 'Zimpligital/uchat-gitops-v2',
      required: true,
    },
    { key: 'ref', label: 'Dispatch branch', placeholder: 'dev', required: true },
    { key: 'workflowFile', label: 'Workflow file', placeholder: 'set-image.yml', required: true },
    { key: 'overlay', label: 'Overlay', placeholder: 'oa-prd', required: true },
    {
      key: 'registry',
      label: 'Registry',
      placeholder: '<account>.dkr.ecr.<region>.amazonaws.com',
      required: true,
    },
    { key: 'argocdUrl', label: 'ArgoCD URL', placeholder: 'https://argocd.uchat.social' },
    { key: 'argocdApp', label: 'ArgoCD app', placeholder: 'uchat-oa-prd' },
  ],
  workflow: [
    {
      key: 'workflowFile',
      label: 'Publish workflow',
      placeholder: 'publish-web.yml',
      required: true,
    },
    {
      key: 'buildWorkflowFile',
      label: 'Build workflow',
      placeholder: 'release-web.yml',
      required: true,
    },
  ],
};

/** Flat form values; images as `name=repository` lines. */
const deployForm = (cfg: DeployConfig | null): Record<string, string> => {
  if (!cfg) return {};
  if (cfg.kind === 'workflow')
    return { workflowFile: cfg.workflowFile, buildWorkflowFile: cfg.buildWorkflowFile };
  return {
    repo: cfg.repo,
    ref: cfg.ref,
    workflowFile: cfg.workflowFile,
    overlay: cfg.overlay,
    registry: cfg.registry,
    images: Object.entries(cfg.images)
      .map(([name, repository]) => `${name}=${repository}`)
      .join('\n'),
    argocdUrl: cfg.argocd?.url ?? '',
    argocdApp: cfg.argocd?.appName ?? '',
  };
};

/** Form values → config; the server validates (bad lines come back as a 422 toast). */
const deployConfigFrom = (kind: DeployConfig['kind'], f: Record<string, string>): DeployConfig => {
  const v = (key: string) => (f[key] ?? '').trim();
  if (kind === 'workflow')
    return { kind, workflowFile: v('workflowFile'), buildWorkflowFile: v('buildWorkflowFile') };
  return {
    kind,
    repo: v('repo'),
    ref: v('ref'),
    workflowFile: v('workflowFile'),
    overlay: v('overlay'),
    registry: v('registry'),
    images: Object.fromEntries(
      v('images')
        .split('\n')
        .filter((line) => line.trim())
        .map((line) => {
          const [name = '', ...repository] = line.split('=');
          return [name.trim(), repository.join('=').trim()];
        }),
    ),
    ...(v('argocdUrl') || v('argocdApp')
      ? { argocd: { url: v('argocdUrl'), appName: v('argocdApp') } }
      : {}),
  };
};

const DeploymentSection = (props: {
  product: ReleaseProductInfo;
  environment: ReleaseEnvironment;
  canManage: boolean;
}) => {
  const toast = useToast();
  const confirmDialog = useConfirm();
  const update = useUpdateReleaseChannel();
  const channel = () => props.product.channels.find((c) => c.environment === props.environment);
  const kind = (): DeployConfig['kind'] =>
    props.product.adapter === 'server' ? 'gitops' : 'workflow';
  // Whole-form draft (null = untouched): the config is replaced as one object.
  const [draft, setDraft] = createSignal<Record<string, string> | null>(null);
  const [token, setToken] = createSignal('');
  const form = () => draft() ?? deployForm(channel()?.deployConfig ?? null);
  const setField = (key: string, value: string) => setDraft({ ...form(), [key]: value });
  const ENV = props.environment.toUpperCase();

  const patch = async (body: UpdateReleaseChannelBody, success: string) => {
    try {
      await update.mutateAsync({
        productId: props.product.id,
        environment: props.environment,
        body,
      });
      setDraft(null);
      setToken('');
      toast.success(success);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // Untouched Save must not write display fallbacks back (UCH-433 guard).
  const save = () => {
    const body: UpdateReleaseChannelBody = {};
    const dirty = draft();
    if (dirty !== null) body.deployConfig = deployConfigFrom(kind(), dirty);
    if (token().trim()) body.deploySecret = token().trim();
    if (Object.keys(body).length > 0) void patch(body, `${ENV} deployment saved`);
  };

  const remove = async () => {
    const ok = await confirmDialog({
      title: `Remove ${ENV} deployment?`,
      body: 'Publish will only mark releases published in this environment, without deploying.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (ok) await patch({ deployConfig: null, deploySecret: null }, `${ENV} deployment removed`);
  };

  return (
    <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <div class="mb-2 flex items-center justify-between gap-2">
        <h3>
          <EnvBadge env={props.environment} />
        </h3>
        <Show when={!channel()?.deployConfig}>
          <span class="text-xs text-neutral-500">
            Not set — Publish only marks releases published
          </span>
        </Show>
      </div>
      <div class="grid gap-2 sm:grid-cols-2">
        <For each={DEPLOY_FIELDS[kind()]}>
          {(field) => (
            <Input
              label={field.label}
              placeholder={field.placeholder}
              required={field.required}
              value={form()[field.key] ?? ''}
              onInput={(e) => setField(field.key, e.currentTarget.value)}
              disabled={!props.canManage}
              aria-label={`${props.environment} ${field.label}`}
            />
          )}
        </For>
      </div>
      <Show when={kind() === 'gitops'}>
        <div class="mt-2">
          <Textarea
            label="Images"
            required
            rows={3}
            placeholder="uchat-oa-svc-core-image=uchat-oa/svc-core"
            hint="One per line: kustomize image name=repository"
            value={form().images ?? ''}
            onInput={(e) => setField('images', e.currentTarget.value)}
            disabled={!props.canManage}
            aria-label={`${props.environment} images`}
          />
        </div>
        <div class="mt-2 flex items-end gap-2">
          <div class="flex-1">
            <Input
              type="password"
              autocomplete="off"
              label="ArgoCD token"
              placeholder={channel()?.hasDeploySecret ? 'Saved — type to replace' : ''}
              value={token()}
              onInput={(e) => setToken(e.currentTarget.value)}
              disabled={!props.canManage}
              aria-label={`${props.environment} ArgoCD token`}
            />
          </div>
          <Show when={channel()?.hasDeploySecret}>
            <span class="pb-2">
              <Badge tone="success">set</Badge>
            </span>
            <Show when={props.canManage}>
              <Button
                variant="ghost"
                onClick={() => patch({ deploySecret: null }, `${ENV} ArgoCD token cleared`)}
                disabled={update.isPending}
              >
                Clear token
              </Button>
            </Show>
          </Show>
        </div>
      </Show>
      <Show when={props.canManage}>
        <div class="mt-3 flex justify-end gap-2">
          <Show when={channel()?.deployConfig}>
            <Button variant="ghost" onClick={remove} disabled={update.isPending}>
              Remove
            </Button>
          </Show>
          <Button variant="secondary" onClick={save} loading={update.isPending}>
            Save
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** Disable hides the product across release surfaces until re-enabled (UCH-294). */
const ProductToggleButton = (props: { product: ReleaseProductInfo }) => {
  const toast = useToast();
  const confirmDialog = useConfirm();
  const update = useUpdateReleaseProduct();
  const disabled = () => props.product.disabledAt !== null;

  const toggle = async () => {
    if (!disabled()) {
      const ok = await confirmDialog({
        title: `Disable ${props.product.name}?`,
        body: 'Hides the product and its releases from release pages until re-enabled. Config stays accessible here.',
        confirmLabel: 'Disable',
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await update.mutateAsync({ productId: props.product.id, body: { disabled: !disabled() } });
      toast.success(disabled() ? 'Product enabled' : 'Product disabled');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Button
      variant={disabled() ? 'primary' : 'danger'}
      size="sm"
      onClick={toggle}
      loading={update.isPending}
    >
      {disabled() ? 'Enable product' : 'Disable product'}
    </Button>
  );
};

const ProductConfig = (props: { product: ReleaseProductInfo }) => {
  const { session } = useAuth();
  const integrations = useReleaseIntegrations();

  const canManage = () => session()?.permissions.has('releases:config:manage') ?? false;
  const canManageProducts = () => session()?.permissions.has('releases:products:manage') ?? false;
  const isDesktop = () => props.product.adapter === 'desktop_app';
  // Stable environment keys: refetches replace channel objects, but rows keyed by the
  // env string keep their local edit state (UCH-295).
  const envs = () => props.product.channels.map((c) => c.environment);
  const channelFor = (env: ReleaseEnvironment) =>
    props.product.channels.find((c) => c.environment === env) as ReleaseChannelInfo;

  return (
    <>
      <PageHeader
        title={props.product.name}
        sub={
          <div class="flex items-center gap-2">
            <p>{props.product.slug}</p>
            <Badge tone="neutral">{RELEASE_ADAPTER_LABELS[props.product.adapter]}</Badge>
            <Show when={props.product.disabledAt}>
              <Badge tone="danger">Disabled</Badge>
            </Show>
          </div>
        }
        actions={
          <Show when={canManageProducts()}>
            <ProductToggleButton product={props.product} />
          </Show>
        }
      />

      <div class="mx-auto max-w-6xl space-y-6 p-6">
        <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
          <h2 class="mb-3 font-medium">Repository</h2>
          <p class="text-sm">
            {props.product.repoOwner && props.product.repoName ? (
              <a
                href={`https://github.com/${props.product.repoOwner}/${props.product.repoName}`}
                target="_blank"
                rel="noreferrer"
                class="text-brand-600 hover:underline dark:text-brand-400"
              >
                <code>
                  {props.product.repoOwner}/{props.product.repoName}
                </code>
              </a>
            ) : (
              <span class="text-neutral-500">
                Not configured — set GITHUB_REPO_
                {props.product.slug.toUpperCase().replace(/-/g, '_')} (see integration setup)
              </span>
            )}
          </p>
        </div>

        <BuildCounterCard product={props.product} canManage={canManage()} />

        <PinnedBranchesCard product={props.product} canManage={canManage()} />

        <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
          <h2 class="mb-2 font-medium">Environments</h2>
          <div class="flex flex-col">
            <For each={envs()}>
              {(env) => <EnvironmentSummary product={props.product} channel={channelFor(env)} />}
            </For>
          </div>
        </div>

        <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
          <h2 class="mb-1 font-medium">Slack notifications</h2>
          <p class="mb-3 text-neutral-500 text-sm">
            Build started / succeeded / failed and publish events post to this channel. Invite the
            bot to the channel first; leave blank to disable.
            <Show when={integrations.data && !integrations.data.data.slack}>
              {' '}
              <span class="text-amber-600 dark:text-amber-400">
                Slack is not configured on this server (SLACK_BOT_TOKEN).
              </span>
            </Show>
          </p>
          <div class="space-y-3">
            <For each={envs()}>
              {(env) => (
                <SlackChannelSection
                  product={props.product}
                  environment={env}
                  canManage={canManage()}
                  configured={integrations.data?.data.slack ?? true}
                />
              )}
            </For>
          </div>
        </div>

        <Show when={isDesktop()}>
          <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
            <h2 class="mb-3 font-medium">Desktop updater</h2>
            <div class="space-y-3">
              <For each={envs()}>
                {(env) => (
                  <DesktopUpdaterSection
                    product={props.product}
                    environment={env}
                    canManage={canManage()}
                  />
                )}
              </For>
            </div>
          </div>
        </Show>

        <Show when={props.product.adapter === 'server' || props.product.adapter === 'web'}>
          <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
            <h2 class="mb-1 font-medium">Deployment</h2>
            <p class="mb-3 text-sm text-neutral-500">
              Publish deploys the build here first and marks it published once the deployment
              succeeds; a failed deployment leaves the release succeeded so Publish can be retried.
            </p>
            <div class="space-y-3">
              <For each={envs()}>
                {(env) => (
                  <DeploymentSection
                    product={props.product}
                    environment={env}
                    canManage={canManage()}
                  />
                )}
              </For>
            </div>
          </div>
        </Show>

        <Show when={props.product.adapter === 'mobile_app'}>
          <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
            <h2 class="mb-3 font-medium">Firebase App Distribution</h2>
            <div class="space-y-3">
              <For each={envs()}>
                {(env) => (
                  <MobileDistributionSection
                    product={props.product}
                    environment={env}
                    canManage={canManage()}
                  />
                )}
              </For>
            </div>
          </div>
        </Show>
      </div>
    </>
  );
};

export default function ReleaseProductDetail() {
  const params = useParams<{ productId: string }>();
  const products = useReleaseProducts(true);

  const product = () => (products.data?.data ?? []).find((p) => p.id === params.productId);

  return (
    <section>
      <Show
        when={!products.isPending}
        fallback={
          <div class="mx-auto max-w-6xl p-6">
            <Skeleton class="h-64" />
          </div>
        }
      >
        <Show
          when={product()}
          fallback={
            <div class="mx-auto max-w-6xl p-6">
              <EmptyState variant="error" title="Product not found" />
            </div>
          }
        >
          {(p) => <ProductConfig product={p()} />}
        </Show>
      </Show>
    </section>
  );
}
